import { useGetFeedbackQuery, useLazyGetFeedbackQuery } from '@consigli/hooks';
import { Button, Label } from '@consigli/shadcn';
import { Feedback, FeedbackType, FeedbackTypeLabels, ServiceNameType } from '@consigli/types';
import { saveBlob } from '@consigli/utils';
import { ColumnDef } from '@tanstack/react-table';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';

import { DataTable, DateRangeFilter, DEFAULT_PAGE_SIZE, Pagination, Select } from '@/components';
import { buildFeedbackCsv, getServiceLabel } from '@/pages/users/feedback/feedback-export';

type FeedbackExportRow = {
  type: string;
  service: string | null;
  message: string;
  userEmail: string;
  createdAt: string;
};

const mapToExportRow = (item: FeedbackExportRow) => ({
  type: item.type,
  service: item.service,
  message: item.message,
  userEmail: item.userEmail,
  createdAt: new Date(item.createdAt).toLocaleDateString(),
});

export const FeedbackTab = () => {
  const pageSize = DEFAULT_PAGE_SIZE;

  // Use current date for "today"
  const todayDate = new Date();
  const today = todayDate.toISOString().split('T')[0];

  // 7 days ago
  const sevenDaysAgoDate = new Date(todayDate);
  sevenDaysAgoDate.setDate(todayDate.getDate() - 7);
  const sevenDaysAgo = sevenDaysAgoDate.toISOString().split('T')[0];

  const ALL_TYPES = 'ALL';
  type TypeFilterOption = FeedbackType | typeof ALL_TYPES;

  const typeFilterOptions: TypeFilterOption[] = [ALL_TYPES, ...Object.values(FeedbackType)];

  const typeFilterLabels: Record<TypeFilterOption, string> = {
    [ALL_TYPES]: 'All types',
    ...FeedbackTypeLabels,
  };

  const ALL_SKILLS = 'ALL';
  type SkillFilterOption = ServiceNameType | typeof ALL_SKILLS;

  const skillFilterOptions: SkillFilterOption[] = [ALL_SKILLS, ...Object.values(ServiceNameType)];

  const [dateFrom, setDateFrom] = useState(sevenDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [selectedType, setSelectedType] = useState<TypeFilterOption>(ALL_TYPES);
  const [selectedSkill, setSelectedSkill] = useState<SkillFilterOption>(ALL_SKILLS);
  const [page, setPage] = useState(1);

  const isSkillFilterEnabled = selectedType === FeedbackType.SKILL;

  const { data, isFetching } = useGetFeedbackQuery({
    page,
    pageSize,
    createdAtFrom: dateFrom,
    createdAtTo: dateTo,
    ...(selectedType !== ALL_TYPES ? { type: selectedType } : {}),
    ...(isSkillFilterEnabled && selectedSkill !== ALL_SKILLS ? { service: selectedSkill } : {}),
  });
  const [triggerGetFeedback, { isFetching: isDownloading }] = useLazyGetFeedbackQuery();

  const columns: ColumnDef<Feedback>[] = useMemo(
    () => [
      {
        header: 'Type',
        accessorKey: 'type',
        cell: ({ row }) => row.original.type,
      },
      {
        header: 'Skill',
        accessorKey: 'service',
        cell: ({ row }) => (row.original.service ? getServiceLabel(row.original.service) : '-'),
      },
      {
        header: 'Message',
        accessorKey: 'message',
        cell: ({ row }) => (
          <div className="whitespace-pre-wrap wrap-break-word max-w-md">{row.original.message}</div>
        ),
      },
      {
        header: 'User Email',
        accessorKey: 'userEmail',
        cell: ({ row }) => row.original.userEmail,
      },
      {
        header: 'Date',
        accessorKey: 'createdAt',
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
      },
    ],
    [],
  );

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
    setPage(1);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
    setPage(1);
  };

  const handleTypeChange = (value: TypeFilterOption) => {
    setSelectedType(value);
    setSelectedSkill(ALL_SKILLS);
    setPage(1);
  };

  const handleSkillChange = (value: SkillFilterOption) => {
    setSelectedSkill(value);
    setPage(1);
  };

  const handleDownloadCsv = async () => {
    try {
      const downloadPageSize = 200;
      const allRows: FeedbackExportRow[] = [];
      let pageToFetch = 1;
      let totalPages = 1;

      while (pageToFetch <= totalPages) {
        const response = await triggerGetFeedback(
          {
            page: pageToFetch,
            pageSize: downloadPageSize,
            createdAtFrom: dateFrom,
            createdAtTo: dateTo,
            ...(selectedType !== ALL_TYPES ? { type: selectedType } : {}),
            ...(isSkillFilterEnabled && selectedSkill !== ALL_SKILLS
              ? { service: selectedSkill }
              : {}),
          },
          true,
        ).unwrap();

        allRows.push(...response.results.map(mapToExportRow));

        if (pageToFetch === 1) {
          totalPages = Math.max(1, Math.ceil(response.count / downloadPageSize));
        }

        pageToFetch += 1;
      }

      if (!allRows.length) {
        return;
      }

      const csvContent = buildFeedbackCsv(allRows);
      const fileName = `feedback-${dateFrom}-to-${dateTo}.csv`;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      saveBlob(fileName, blob);
    } catch (error) {
      console.error('Failed to download feedback export.', error);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 items-end justify-between">
        <div className="flex gap-4 items-end">
          <DateRangeFilter
            from={dateFrom}
            to={dateTo}
            onFromChange={handleDateFromChange}
            onToChange={handleDateToChange}
            minDate="2026-01-01"
            maxDate={today}
          />
          <div className="flex flex-row items-center gap-1.5">
            <Label htmlFor="type-filter">Type</Label>
            <Select
              options={typeFilterOptions}
              value={selectedType}
              onValueChange={handleTypeChange}
              getOptionLabel={(option) => typeFilterLabels[option]}
              getOptionValue={(option) => option}
            />
          </div>
          <div className="flex flex-row items-center gap-1.5">
            <Label htmlFor="skill-filter" className={!isSkillFilterEnabled ? 'opacity-50' : ''}>
              Skill
            </Label>
            <Select
              options={skillFilterOptions}
              value={selectedSkill}
              onValueChange={handleSkillChange}
              getOptionLabel={(option) =>
                option === ALL_SKILLS ? 'All skills' : getServiceLabel(option)
              }
              getOptionValue={(option) => option}
              disabled={!isSkillFilterEnabled}
              showSearch
            />
          </div>
        </div>
        <Button
          onClick={handleDownloadCsv}
          disabled={isFetching || isDownloading}
          className="cursor-pointer"
        >
          <Download className="h-4 w-4" />
          Download CSV
        </Button>
      </div>
      <DataTable
        height="60vh"
        columns={columns}
        data={data?.results ?? []}
        isLoading={isFetching}
        scrollable
      />
      <Pagination
        onPageChange={setPage}
        pageSize={pageSize}
        currentPage={page}
        totalCount={data?.count ?? 0}
      />
    </div>
  );
};
